import { BLOB_ID } from '../constants/blobId';
import { ERequestApiStatus } from '../types/appState';
import { TChatImages } from '../types/chatFiles';
import { IChatHeader } from '../types/chatHeader';
import { TChatHint, TChatHintBanner } from '../types/chatHint';
import { ICustomChatParams } from '../types/customChatParams';
import { TOffer } from '../types/offer';
import { TFrameCompanion, TOpenedChat, TOpenedChats } from '../types/openedChats';

export type TNewChat = {
  agentId?: number | null;
  offerData?: TOffer;
  hintQuestions?: TChatHint[];
  hintQuestionBanner?: TChatHintBanner | null;
  frameCompanion?: TFrameCompanion;
  defaultMessage?: string;
  defaultMessagePayload?: string;
  sendDefaultMessage?: boolean;
  noDefaultMessageIfChatExists?: boolean;
  source?: string;
  gaProducts?: unknown;
  gaExtra?: object;
  checkin?: string | null;
  checkout?: string | null;
  status?: ERequestApiStatus;
  isOpponentContactsVisible?: boolean;
  customChatParams?: ICustomChatParams;
};

type TAddNewChatProps = {
  openedChats: TOpenedChats;
  offer?: TOffer;
  currentChatId: string;
  chatImages?: TChatImages | null;
  newChat?: TNewChat;
  header?: IChatHeader;
};

export const addNewChat = ({
  openedChats,
  offer,
  currentChatId,
  newChat,
  chatImages,
  header,
}: TAddNewChatProps): TOpenedChats => {
  if (
    typeof openedChats[currentChatId] === 'object' &&
    'chatMessages' in openedChats[currentChatId] &&
    'currentTimestamp' in openedChats[currentChatId]
  ) {
    return {
      ...openedChats,
      [currentChatId]: {
        ...openedChats[currentChatId],
        status: ERequestApiStatus.Pending,
      },
    };
  }

  const {
    agentId,
    offerData,
    frameCompanion,
    defaultMessage,
    defaultMessagePayload,
    sendDefaultMessage,
    noDefaultMessageIfChatExists,
    hintQuestions = [],
    hintQuestionBanner = null,
    source,
    gaProducts,
    gaExtra,
    checkin,
    checkout,
    status,
    isOpponentContactsVisible,
    customChatParams,
  } = newChat || {};

  const chatExists = currentChatId !== BLOB_ID;

  const newOpenedChat: TOpenedChat = {
    agentId,
    chatId: currentChatId as string,
    currentTimestamp: undefined,
    offer: offer || offerData,
    header,
    firstMessageStatus: null,
    newChatId: null,
    chatMessages: [],
    pendingFiles: null,
    images: chatImages || null,
    frameCompanion,
    editableMessage: noDefaultMessageIfChatExists && chatExists ? '' : defaultMessage,
    defaultMessagePayload,
    sendDefaultMessage,
    hintQuestions: {
      hints: hintQuestions,
      banner: hintQuestionBanner,
      isVisible: hintQuestions.length > 0,
    },
    ga: {
      source,
      products: gaProducts,
      ...gaExtra,
    },
    availableDates: {
      checkin,
      checkout,
    },
    status: status ?? ERequestApiStatus.Pending,
    error: null,
    isOpponentContactsVisible: isOpponentContactsVisible ?? true,
    customChatParams,
  };

  return {
    ...openedChats,
    [currentChatId]: newOpenedChat,
  };
};
